import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/home';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const total = heroSlides.length;

  const goTo = useCallback((index: number) => {
    setCurrent((index + total) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo(current + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  return (
    <section className="relative w-full h-[520px] md:h-[680px] overflow-hidden">
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-all duration-700 ${
            i === current ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-950"></div>
        </div>
      ))}

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-50 mb-4 leading-tight tracking-wide">
              {heroSlides[current].title}
            </h1>
            <p className="text-base md:text-lg text-foreground-400 mb-8 max-w-xl mx-auto leading-relaxed">
              {heroSlides[current].subtitle}
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to={heroSlides[current].link}
                className="px-8 py-3 text-sm font-medium rounded-lg bg-primary-500 text-background-950 hover:bg-primary-600 transition-all whitespace-nowrap"
              >
                <i className="ri-play-circle-line mr-2"></i>
                {heroSlides[current].cta}
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 text-sm font-medium rounded-lg border border-foreground-400/30 text-foreground-200 hover:border-primary-500 hover:text-primary-500 transition-all whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
              i === current ? 'bg-primary-500 w-8' : 'bg-foreground-400/40 hover:bg-foreground-400/70'
            }`}
            aria-label={`切换到第 ${i + 1} 张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo(current - 1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-950/60 text-foreground-300 hover:text-primary-500 hover:bg-background-950/80 transition-all z-10 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={() => goTo(current + 1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-950/60 text-foreground-300 hover:text-primary-500 hover:bg-background-950/80 transition-all z-10 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}